import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import { CONFIG } from 'src/global-config';
import { Image } from 'src/components/image';
import { AnimateCountUp } from 'src/components/animate';
import { Iconify } from 'src/components/iconify';
import { LazyMotion, domAnimation, m } from 'framer-motion';
import { useLocale } from 'src/locales';

// ----------------------------------------------------------------------

const decimalDescriptors = ['Tons_Moved', 'Cargo_Value', 'Operations'];

const SUMMARY = [
  { total: 5000, description: 'Clients', icon: 'mdi:account-group', toFixed: 0 },
  { total: 234000, description: 'Trips_Completed', icon: 'mdi:package-variant-closed', toFixed: 0 },
  { total: 2600000, description: 'Tons_Moved', icon: 'iconoir:truck', toFixed: 1 },
  {
    total: 115800000,
    description: 'Operations',
    icon: 'streamline-ultimate:trip-road',
    toFixed: 1,
  },
];

const SUMMARY_sec = [
  { total: 30400000000, description: 'Cargo_Value', icon: 'lucide:saudi-riyal', toFixed: 1 },

  {
    total: 300,
    description: 'Destinations_Served',
    icon: 'majesticons:map-simple-marker-line',
    toFixed: 0,
  },
  { total: 5, description: 'Years_Experience', icon: 'mingcute:time-duration-line', toFixed: 0 },
];

// Motion Variants
const textVariants = {
  hidden: { opacity: 0, y: 100 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } },
};

const listContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.2,
      delayChildren: 0.2,
    },
  },
};

const itemVariant = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } },
};

// ----------------------------------------------------------------------

export function WhoWeAre({ sx, ...other }) {
  const language = localStorage.getItem('i18nextLng') || 'ar';
  const isRTL = language === 'ar';
  const { t } = useLocale();
  const renderTexts = () => (
    <m.div variants={textVariants}>
      <Box sx={{ mx: 'auto', maxWidth: 700, mb: { xs: 5, md: 5 } }}>
        <Typography variant="h1" component="h1" sx={{ fontSize: '2rem !important' }}>
          {t('Transforming_Transport_One_Trip_at_a_Time')}
        </Typography>
        <Typography sx={{ mt: 3, color: 'text.secondary' }}>
          {t(
            'Our_growing_numbers_reflect_smarter_deliveries_optimized_operations_and_trusted_partnerships_across_industries'
          )}
        </Typography>
      </Box>
    </m.div>
  );

  const renderList = () => (
    <m.div variants={listContainer}>
      <Box
        sx={{
          gap: 3,
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {SUMMARY?.map((value) => {
          const isIconify = typeof value?.icon === 'string' && value?.icon.includes(':');

          return (
            <m.div key={value?.description} variants={itemVariant}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  color: 'text.secondary',
                }}
              >
                {isIconify ? (
                  <Iconify icon={value?.icon} width={64} sx={{ mb: 3, color: 'primary.main' }} />
                ) : (
                  <Image
                    alt={value?.description}
                    src={value?.icon}
                    disablePlaceholder
                    ratio="1/1"
                    sx={{ mb: 3, width: 80 }}
                    priority
                  />
                )}

                {/* Number + Plus Sign */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {isRTL && (
                    <Box
                      component="span"
                      sx={{
                        color: 'primary.main',
                        typography: 'h4',
                        lineHeight: 1,
                        // ml: 0.3,
                      }}
                    >
                      +
                    </Box>
                  )}
                  <AnimateCountUp
                    variant="h3"
                    to={value?.total}
                    toFixed={value?.toFixed || 0}
                    sx={{ color: 'text.primary', mr: 0.5, textTransform: 'capitalize' }}
                  />
                  {!isRTL && (
                    <Box
                      component="span"
                      sx={{
                        color: 'primary.main',
                        typography: 'h4',
                        lineHeight: 1,
                        // ml: 0.3,
                      }}
                    >
                      +
                    </Box>
                  )}
                </Box>

                <Typography sx={{ color: 'text.secondary' }}>{t(value?.description)}</Typography>
              </Box>
            </m.div>
          );
        })}
      </Box>
      <Box
        sx={{
          gap: 3,
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(3, 0.25fr)' },
          alignItems: 'center',
          justifyContent: 'center',
          mt: 3,
        }}
      >
        {SUMMARY_sec?.map((value) => {
          const isIconify = typeof value.icon === 'string' && value.icon.includes(':');

          return (
            <m.div key={value?.description} variants={itemVariant}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  flexDirection: 'column',
                  color: 'text.secondary',
                }}
              >
                {isIconify ? (
                  <Iconify icon={value?.icon} width={64} sx={{ mb: 3, color: 'primary.main' }} />
                ) : (
                  <Image
                    alt={value?.description}
                    src={value?.icon}
                    disablePlaceholder
                    ratio="1/1"
                    sx={{ mb: 3, width: 80 }}
                    priority
                  />
                )}

                {/* Number + Plus Sign */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {isRTL && (
                    <Box
                      component="span"
                      sx={{
                        color: 'primary.main',
                        typography: 'h4',
                        lineHeight: 1,
                        // ml: 0.3,
                      }}
                    >
                      +
                    </Box>
                  )}
                  <AnimateCountUp
                    variant="h3"
                    to={value?.total}
                    toFixed={value?.toFixed || 0}
                    sx={{ color: 'text.primary', mr: 0.5, textTransform: 'capitalize' }}
                  />
                  {!isRTL && (
                    <Box
                      component="span"
                      sx={{
                        color: 'primary.main',
                        typography: 'h4',
                        lineHeight: 1,
                        // ml: 0.3,
                      }}
                    >
                      +
                    </Box>
                  )}
                </Box>

                <Typography sx={{ color: 'text.secondary' }}>{t(value.description)}</Typography>
              </Box>
            </m.div>
          );
        })}
      </Box>
    </m.div>
  );

  return (
    <LazyMotion features={domAnimation}>
      <Box
        component="section"
        sx={[
          {
            textAlign: 'center',
            py: { xs: 5, md: 10 },
            overflow: 'hidden',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...other}
      >
        <Container>
          <m.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }}>
            {renderTexts()}
            {renderList()}
          </m.div>
        </Container>
      </Box>
    </LazyMotion>
  );
}
